import React, { useState } from 'react';
import { AlertTriangle, Mail, Shield, CheckCircle, XCircle } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { useTranslation } from '../../../i18n/hooks/useTranslation';

interface AccountClosureProps {
  accountId: string;
}

export const AccountClosure: React.FC<AccountClosureProps> = ({ accountId }) => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [step, setStep] = useState<'warning' | 'otp' | '2fa' | 'success' | 'error'>('warning');
  const [otpCode, setOtpCode] = useState('');
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [twoFactorMethod, setTwoFactorMethod] = useState<'totp' | 'sms'>('totp');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [requires2FA, setRequires2FA] = useState(false);
  const [hasAuthenticator, setHasAuthenticator] = useState(false);
  const [hasSMS, setHasSMS] = useState(false);
  const [message, setMessage] = useState('');

  const handleInitiateClosure = async () => {
    setIsLoading(true);
    setError('');
    setMessage('');

    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const url = `${apiUrl}/api/accounts/${accountId}/close/initiate`;
      console.log('🔍 [AccountClosure] Initiate URL:', url);
      console.log('🔍 [AccountClosure] Account ID:', accountId);
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      const data = await response.json();
      console.log('🔍 [AccountClosure] Response:', data);

      if (!data.success) {
        console.error('❌ [AccountClosure] Error:', data.message);
        setError(data.message || t('account.close.initiateFailed'));
        return;
      }

      setRequires2FA(data.requires2FA || false);
      setHasAuthenticator(data.hasAuthenticator || false);
      setHasSMS(data.hasSMS || false);
      setMessage(data.message || t('account.close.otpSent'));
      setStep('otp');
    } catch (err: any) {
      setError(err.message || t('account.close.initiateFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendSMSCode = async () => {
    setIsLoading(true);
    setError('');

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/accounts/${accountId}/close/send-sms-code`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      const data = await response.json();

      if (!data.success) {
        setError(data.message || t('account.close.smsFailed'));
        return;
      }

      setMessage(data.message || t('account.close.smsSent'));
    } catch (err: any) {
      setError(err.message || t('account.close.smsFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (!otpCode) {
      setError(t('account.close.otpRequired'));
      return;
    }

    setIsLoading(true);
    setError('');

    // If 2FA is required, move to 2FA step
    if (requires2FA) {
      setStep('2fa');
      setIsLoading(false);
      return;
    }

    // Otherwise, verify and close immediately
    await handleVerifyAndClose();
  };

  const handleVerifyAndClose = async () => {
    setIsLoading(true);
    setError('');

    try {
      const body: any = {
        otpCode
      };

      if (requires2FA && twoFactorCode) {
        body.twoFactorCode = twoFactorCode;
        body.twoFactorMethod = twoFactorMethod;
      }

      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const url = `${apiUrl}/api/accounts/${accountId}/close/verify`;
      console.log('🔍 [AccountClosure] Verify URL:', url);
      console.log('🔍 [AccountClosure] Request body:', body);

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(body)
      });

      console.log('🔍 [AccountClosure] Response status:', response.status);
      console.log('🔍 [AccountClosure] Response headers:', Object.fromEntries(response.headers.entries()));

      if (!response.ok) {
        // Try to parse JSON error, but handle HTML responses
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const data = await response.json();
          console.error('❌ [AccountClosure] Error response:', data);
          setError(data.message || t('account.close.verifyFailed'));
        } else {
          const text = await response.text();
          console.error('❌ [AccountClosure] Non-JSON error response:', text.substring(0, 200));
          setError(`Server error: ${response.status} ${response.statusText}`);
        }
        setStep('error');
        return;
      }

      const data = await response.json();
      console.log('🔍 [AccountClosure] Success response:', data);

      if (!data.success) {
        console.error('❌ [AccountClosure] Error:', data.message);
        setError(data.message || t('account.close.verifyFailed'));
        setStep('error');
        return;
      }

      setStep('success');
      setMessage(data.message || t('account.close.success'));
      
      // Redirect to login after 5 seconds
      setTimeout(() => {
        localStorage.removeItem('token');
        // Redirect to login page instead of home to avoid SSO issues
        window.location.href = '/login';
      }, 5000);
    } catch (err: any) {
      console.error('❌ [AccountClosure] Exception:', err);
      setError(err.message || t('account.close.verifyFailed'));
      setStep('error');
    } finally {
      setIsLoading(false);
    }
  };

  if (step === 'success') {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-600">
        <div className="flex items-center justify-center mb-4">
          <CheckCircle className="w-16 h-16 text-success-500" />
        </div>
        <h2 className="text-2xl font-bold text-center mb-4 text-gray-900 dark:text-gray-100">{t('account.close.successTitle')}</h2>
        <p className="text-gray-600 dark:text-gray-400 text-center mb-6">{message}</p>
        <p className="text-sm text-gray-500 dark:text-gray-400 text-center">{t('account.close.redirecting')}</p>
      </div>
    );
  }

  if (step === 'error') {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-600">
        <div className="flex items-center justify-center mb-4">
          <XCircle className="w-16 h-16 text-error-500" />
        </div>
        <h2 className="text-2xl font-bold text-center mb-4 text-gray-900 dark:text-gray-100">{t('account.close.errorTitle')}</h2>
        <p className="text-error-600 dark:text-error-400 text-center mb-6">{error}</p>
        <button
          onClick={() => {
            setStep('warning');
            setError('');
            setOtpCode('');
            setTwoFactorCode('');
          }}
          className="w-full bg-gray-500 dark:bg-gray-600 text-white py-2 px-4 rounded hover:bg-gray-600 dark:hover:bg-gray-700"
        >
          {t('common.back')}
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-600">
      <h2 className="text-2xl font-bold mb-6 flex items-center text-gray-900 dark:text-gray-100">
        <AlertTriangle className="w-6 h-6 text-error-500 mr-2" />
        {t('account.close.title')}
      </h2>

      {step === 'warning' && (
        <div>
          <div className="bg-red-50 dark:bg-gray-700 dark:border dark:border-error-800/50 border-l-4 border-red-500 dark:border-red-500 p-4 mb-6">
            <div className="flex">
              <AlertTriangle className="w-5 h-5 text-error-500 mr-2" />
              <div>
                <h3 className="text-error-800 dark:text-error-300 font-semibold mb-2">{t('account.close.warningTitle')}</h3>
                <p className="text-error-700 dark:text-error-300 mb-2">{t('account.close.warningDescription')}</p>
                <ul className="list-disc list-inside text-error-700 dark:text-error-300 space-y-1">
                  <li>{t('account.close.warningItem1')}</li>
                  <li>{t('account.close.warningItem2')}</li>
                  <li>{t('account.close.warningItem3')}</li>
                  <li>{t('account.close.warningItem4')}</li>
                  <li>{t('account.close.warningItem5')}</li>
                </ul>
                <p className="text-error-800 dark:text-error-300 font-bold mt-2">{t('account.close.warningFinal')}</p>
              </div>
            </div>
          </div>

          {error && (
            <div className="bg-red-50 dark:bg-gray-700 dark:border dark:border-error-800/50 border-l-4 border-red-500 dark:border-red-500 p-4 mb-6">
              <p className="text-error-700 dark:text-error-300">{error}</p>
            </div>
          )}

          <button
            onClick={handleInitiateClosure}
            disabled={isLoading}
            className="w-full bg-error-600 text-white py-3 px-4 rounded hover:bg-error-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          >
            {isLoading ? t('common.sending') : t('account.close.initiate')}
          </button>
        </div>
      )}

      {step === 'otp' && (
        <div>
          {message && (
            <div className="bg-green-50 dark:bg-gray-700 dark:border dark:border-success-800/50 border-l-4 border-green-500 dark:border-green-500 p-4 mb-6">
              <p className="text-success-700 dark:text-success-300">{message}</p>
            </div>
          )}

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('account.close.enterOTP')}
            </label>
            <div className="flex justify-center gap-2 mb-2">
              {[0, 1, 2, 3, 4, 5].map((index) => (
                <input
                  key={index}
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={otpCode[index] || ''}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 1);
                    if (value) {
                      const newCode = otpCode.split('');
                      newCode[index] = value;
                      const updatedCode = newCode.join('').slice(0, 6);
                      setOtpCode(updatedCode);
                      // Auto-focus next input
                      if (index < 5 && value) {
                        const nextInput = document.querySelector(`input[data-otp-index="${index + 1}"]`) as HTMLInputElement;
                        nextInput?.focus();
                      }
                    } else {
                      const newCode = otpCode.split('');
                      newCode[index] = '';
                      setOtpCode(newCode.join(''));
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Backspace' && !otpCode[index] && index > 0) {
                      const prevInput = document.querySelector(`input[data-otp-index="${index - 1}"]`) as HTMLInputElement;
                      prevInput?.focus();
                    }
                  }}
                  onPaste={(e) => {
                    e.preventDefault();
                    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
                    setOtpCode(pastedData);
                    if (pastedData.length === 6) {
                      const lastInput = document.querySelector(`input[data-otp-index="5"]`) as HTMLInputElement;
                      lastInput?.focus();
                    }
                  }}
                  data-otp-index={index}
                  className="w-12 h-14 text-center text-2xl font-semibold border-2 border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  maxLength={1}
                />
              ))}
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 text-center">{t('account.close.otpHint')}</p>
          </div>

          {requires2FA && (
            <div className="bg-yellow-50 dark:bg-gray-700 dark:border dark:border-warning-800/50 border-l-4 border-yellow-500 dark:border-yellow-500 p-4 mb-6">
              <div className="flex items-start">
                <Shield className="w-5 h-5 text-warning-600 dark:text-warning-400 mr-2 mt-0.5" />
                <div>
                  <h3 className="text-warning-800 dark:text-warning-300 font-semibold mb-1">{t('account.close.twoFactorRequired')}</h3>
                  <p className="text-warning-700 dark:text-warning-300 text-sm">{t('account.close.twoFactorDescription')}</p>
                </div>
              </div>
            </div>
          )}

          {error && (
            <div className="bg-red-50 dark:bg-gray-700 dark:border dark:border-error-800/50 border-l-4 border-red-500 dark:border-red-500 p-4 mb-6">
              <p className="text-error-700 dark:text-error-300">{error}</p>
            </div>
          )}

          <button
            onClick={handleVerifyOTP}
            disabled={isLoading || !otpCode || otpCode.length !== 6}
            className="w-full bg-error-600 text-white py-3 px-4 rounded hover:bg-error-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? t('common.verifying') : t('account.close.verifyOTP')}
          </button>
        </div>
      )}

      {step === '2fa' && (
        <div>
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('account.close.select2FAMethod')}
            </label>
            <div className="flex gap-4 mb-4">
              {hasAuthenticator && (
                <button
                  onClick={() => setTwoFactorMethod('totp')}
                  className={`flex-1 py-2 px-4 rounded border ${
                    twoFactorMethod === 'totp'
                      ? 'border-primary-500 bg-primary-50 dark:bg-gray-700 dark:border dark:border-primary-800/50 text-primary-700 dark:text-primary-300'
                      : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                  }`}
                >
                  {t('auth.twoFactor.authenticatorApp')}
                </button>
              )}
              {hasSMS && (
                <button
                  onClick={() => {
                    setTwoFactorMethod('sms');
                    handleSendSMSCode();
                  }}
                  className={`flex-1 py-2 px-4 rounded border ${
                    twoFactorMethod === 'sms'
                      ? 'border-primary-500 bg-primary-50 dark:bg-gray-700 dark:border dark:border-primary-800/50 text-primary-700 dark:text-primary-300'
                      : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                  }`}
                >
                  {t('auth.twoFactor.sms')}
                </button>
              )}
            </div>

            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {twoFactorMethod === 'totp' ? t('auth.twoFactor.enterCode') : t('auth.twoFactor.enterSMSCode')}
            </label>
            <div className="flex justify-center gap-2 mb-2">
              {[0, 1, 2, 3, 4, 5].map((index) => (
                <input
                  key={index}
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={twoFactorCode[index] || ''}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 1);
                    if (value) {
                      const newCode = twoFactorCode.split('');
                      newCode[index] = value;
                      const updatedCode = newCode.join('').slice(0, 6);
                      setTwoFactorCode(updatedCode);
                      // Auto-focus next input
                      if (index < 5 && value) {
                        const nextInput = document.querySelector(`input[data-2fa-index="${index + 1}"]`) as HTMLInputElement;
                        nextInput?.focus();
                      }
                    } else {
                      const newCode = twoFactorCode.split('');
                      newCode[index] = '';
                      setTwoFactorCode(newCode.join(''));
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Backspace' && !twoFactorCode[index] && index > 0) {
                      const prevInput = document.querySelector(`input[data-2fa-index="${index - 1}"]`) as HTMLInputElement;
                      prevInput?.focus();
                    }
                  }}
                  onPaste={(e) => {
                    e.preventDefault();
                    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
                    setTwoFactorCode(pastedData);
                    if (pastedData.length === 6) {
                      const lastInput = document.querySelector(`input[data-2fa-index="5"]`) as HTMLInputElement;
                      lastInput?.focus();
                    }
                  }}
                  data-2fa-index={index}
                  className="w-12 h-14 text-center text-2xl font-semibold border-2 border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  maxLength={1}
                />
              ))}
            </div>

            {twoFactorMethod === 'sms' && (
              <button
                onClick={handleSendSMSCode}
                disabled={isLoading}
                className="mt-2 text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
              >
                {t('common.resend')}
              </button>
            )}
          </div>

          {message && (
            <div className="bg-green-50 dark:bg-gray-700 dark:border dark:border-success-800/50 border-l-4 border-green-500 dark:border-green-500 p-4 mb-6">
              <p className="text-success-700 dark:text-success-300">{message}</p>
            </div>
          )}

          {error && (
            <div className="bg-red-50 dark:bg-gray-700 dark:border dark:border-error-800/50 border-l-4 border-red-500 dark:border-red-500 p-4 mb-6">
              <p className="text-error-700 dark:text-error-300">{error}</p>
            </div>
          )}

          <div className="flex gap-4">
            <button
              onClick={() => {
                setStep('otp');
                setTwoFactorCode('');
                setError('');
              }}
              className="flex-1 bg-gray-500 dark:bg-gray-600 text-white py-3 px-4 rounded hover:bg-gray-600 dark:hover:bg-gray-700"
            >
              {t('common.back')}
            </button>
            <button
              onClick={handleVerifyAndClose}
              disabled={isLoading || !twoFactorCode || twoFactorCode.length !== 6}
              className="flex-1 bg-error-600 text-white py-3 px-4 rounded hover:bg-error-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? t('common.verifying') : t('account.close.confirmClosure')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

